import React, { forwardRef, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import SwiperCore, { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import classnames from 'classnames'

import { IMAGE_MODERATION_STATUSES } from 'constants/media'
import { backdropFilter } from 'helpers/browser'
import { LeftArrowIcon, ProfileImageWithAspectRatio, RightArrowIcon } from 'components'

SwiperCore.use([Navigation])

const NAV_POSITION = {
  TOP: 'top',
  BOTTOM: 'bottom',
}

const NavButton = forwardRef((props, ref) => (
  <button
    type="button"
    className="relative z-10 w-10 h-10 p-2.5 flex justify-between items-center bg-black bg-opacity-50 text-white outline-none rounded-full cursor-pointer focus:ring disabled:opacity-40"
    style={backdropFilter('blur(32.6194px)')}
    {...props}
    ref={ref}
  />
))

const PhotoGallery = ({ images, navPosition, pagination, ImageRenderer }) => {
  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const photos = useMemo(
    () => [
      ...images
        .filter(({ url }) => !!url)
        .filter(({ moderationStatus }) => moderationStatus !== IMAGE_MODERATION_STATUSES.REJECTED)
        .sort(a => (a.profileImage ? -1 : 0)),
    ],
    [images]
  )

  const Renderer = useMemo(() => ImageRenderer || ProfileImageWithAspectRatio, [ImageRenderer])

  return (
    <div className="relative w-full" style={{ minHeight: '250px' }}>
      <div
        className={classnames('absolute w-full px-2 z-10', {
          hidden: photos.length <= 1,
          'top-6': navPosition === NAV_POSITION.TOP,
          'bottom-2.5': navPosition === NAV_POSITION.BOTTOM,
        })}
      >
        <div className="w-full flex justify-between mb-5 relative">
          <NavButton ref={prevRef} disabled={activeSlideIndex === 0}>
            <LeftArrowIcon className="w-full" />
          </NavButton>
          <NavButton ref={nextRef} disabled={activeSlideIndex === images.length - 1}>
            <RightArrowIcon className="w-full" />
          </NavButton>
          {pagination && (
            <div
              className={classnames(
                'absolute z-50 px-0.5 p-1 w-full space-x-1.5 flex justify-around',
                {
                  '-top-4': navPosition === NAV_POSITION.TOP,
                  '-bottom-4': navPosition === NAV_POSITION.BOTTOM,
                }
              )}
            >
              {photos.map((image, index) => (
                <div
                  key={index}
                  className={classnames('h-1 flex-1 bg-white', {
                    'opacity-30': activeSlideIndex !== index,
                  })}
                />
              ))}
            </div>
          )}
        </div>
        <div className="w-full flex justify-around">
          {photos.map((_, index) => (
            <div
              key={index}
              className={classnames('flex-1 bg-white ml-1 mr-1 h1', {
                'opacity-30': activeSlideIndex !== index,
              })}
            />
          ))}
        </div>
      </div>
      <div>
        <Swiper
          onInit={swiper => {
            swiper.params.navigation.prevEl = prevRef.current
            swiper.params.navigation.nextEl = nextRef.current
            swiper.navigation.update()
          }}
          onSlideChange={swiper => {
            setActiveSlideIndex(swiper.activeIndex)
          }}
        >
          {photos.map((image, index) => (
            <SwiperSlide key={index}>
              <Renderer className="w-full" src={image.url} alt="profile" />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
}

PhotoGallery.defaultProps = {
  navPosition: 'bottom',
}

PhotoGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
    })
  ),
  navPosition: PropTypes.string,
  pagination: PropTypes.bool,
  ImageRenderer: PropTypes.func,
}

export default PhotoGallery
